import * as React from "react";
import * as ToggleGroup from "@radix-ui/react-toggle-group";
import { cva } from "class-variance-authority";
import { cn } from "@/shared/utils";
import { Badge } from ".";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangle } from "@fortawesome/sharp-solid-svg-icons/faTriangle";
import { ReimbursementTipVariant } from "../CoverageEditor/CoverageParameterControls";
import { AppLayerContext } from "@/shared/contexts/AppLayer";

export interface RadioButtonGroupProps {
    initialValue?: string;
    onValueChange?: (value: string) => void;
    options: { value: string; label: string | React.ReactNode; icon?: React.ReactNode; id?: string; tipText?: string; dataTestId?: string }[];
    disabled?: boolean;
    error?: string;
    styles?: {
        item?: string;
        label?: string;
    };
    className?: string;
    variant?: "default" | "icon" | "floating" | "floating-inner" | "buttons";
    radioGroupItemClassName?: string;
    reimbursementVariant?: ReimbursementTipVariant;
    showReimbursementTip?: boolean;
    ariaLabelledBy?: string;
}

const radioGroupRoot = cva("flex w-full flex-row rounded-md text-xs text-content-primary disabled:cursor-wait", {
    variants: {
        variant: {
            default: "h-11",
            icon: "gap-6 px-1",
            floating: "h-11",
            "floating-inner": "h-12",
            buttons: "h-auto flex-wrap gap-2"
        }
    },
    defaultVariants: {
        variant: "default"
    }
});

const radioGroupItem = cva("flex cursor-pointer items-center justify-center whitespace-nowrap text-center focus:outline-none disabled:cursor-wait", {
    variants: {
        variant: {
            default: "flex min-w-0 flex-1 flex-col px-1",
            icon: "flex flex-col items-center",
            floating: "flex min-w-0 flex-1 flex-col px-1",
            "floating-inner": "flex min-w-0 flex-1 flex-col px-1",
            buttons: "flex h-auto min-w-fit flex-shrink-0 items-center justify-center rounded-full px-4 py-3 text-content-secondary"
        },
        state: {
            default: "border-2 border-stroke-transparent bg-background-secondary hover:bg-background-tertiary",
            selected: "border-2 border-stroke-brand-b bg-background-primary font-medium",
            iconDefault: "bg-background-primary text-content-inactive",
            iconSelected: "bg-background-primary text-content-primary",
            buttonsDefault: "bg-background-secondary hover:bg-background-tertiary",
            buttonsSelected: "bg-background-brand-b-primary font-medium text-content-primary-invert"
        }
    },
    defaultVariants: {
        variant: "default",
        state: "default"
    }
});

export const RadioButtonGroup: React.FC<RadioButtonGroupProps> = ({
    initialValue = "",
    onValueChange,
    options,
    className,
    error,
    disabled = false,
    variant = "default",
    styles,
    radioGroupItemClassName,
    reimbursementVariant,
    showReimbursementTip,
    ariaLabelledBy
}) => {
    // The selected value is the `initialValue` prop, full stop. This used to be mirrored into state
    // with an effect whose deps included the state itself (`[initialValue, value]`), so any local
    // divergence was immediately snapped back to the prop — the mirror only ever produced a one-frame
    // flash of a selection the owner had not accepted. Reading the prop directly drops the flash and
    // keeps the same settled selection.
    const value = initialValue;
    const hasTipText = options.some(option => !!option?.tipText && option.tipText.length > 0);

    // A/B test (showButtonStyle): restyle the selected button. Read defensively via useContext so this
    // shared ui primitive still renders when used outside an AppLayerProvider (tests/storybook).
    const showButtonStyle = React.useContext(AppLayerContext)?.appState.showButtonStyle ?? false;

    const conditionalWrapperClass = {
        "pb-5": !!hasTipText && !error && reimbursementVariant === "descriptor" && showReimbursementTip,
        "pb-2": !!hasTipText && !error && reimbursementVariant === "affordance" && showReimbursementTip
    };

    const handleChange = (newValue: string) => {
        if (newValue && onValueChange) {
            onValueChange(newValue);
        }
    };

    return (
        <div className={cn(conditionalWrapperClass, className)}>
            <ToggleGroup.Root
                type="single"
                value={value}
                onValueChange={handleChange}
                disabled={disabled}
                className={cn(radioGroupRoot({ variant }))}
                data-testid="radio-group"
                aria-labelledby={ariaLabelledBy}
            >
                {options.map((option, index) => {
                    const isSelected = value === option.value;
                    const tipText = option?.tipText;

                    // Fixed state logic for buttons variant
                    const getVariantState = () => {
                        if (variant === "icon") {
                            return isSelected ? "iconSelected" : "iconDefault";
                        }
                        if (variant === "buttons") {
                            return isSelected ? "buttonsSelected" : "buttonsDefault";
                        }
                        return isSelected ? "selected" : "default";
                    };
                    const variantState = getVariantState();

                    // A/B test (showButtonStyle) overrides, applied at the item level so the label span inherits
                    // the text color. The focus:* variants keep text/border consistent on focus, overriding the
                    // brand-b border on `selected` and the floating-inner `focus:text-content-primary` rule below
                    // (twMerge picks the later class). The buttons variant has no border in the base cva, so we
                    // add `border-2` here too: a primary border when selected, transparent→primary on focus.
                    const getButtonStyleOverride = () => {
                        if (!showButtonStyle) return undefined;
                        if (variantState === "selected") {
                            return "bg-background-brand-b-primary text-content-primary-invert focus:text-content-primary-invert border-stroke-secondary-invert focus:border-stroke-secondary-invert";
                        }
                        // Unselected floating segment: the base cva leaves the border transparent. Per Figma, show a
                        // stroke-primary border and a small shadow.
                        if (variantState === "default") {
                            return "border border-stroke-primary focus:border-stroke-secondary-invert shadow-sm";
                        }
                        if (variantState === "buttonsSelected") {
                            return "border-2 border-stroke-secondary-invert focus:border-stroke-secondary-invert";
                        }
                        if (variantState === "buttonsDefault") {
                            return "border-2 border-stroke-secondary focus:border-stroke-secondary-invert";
                        }
                        return undefined;
                    };
                    const buttonStyleOverride = getButtonStyleOverride();

                    // A/B test (showButtonStyle): Figma layers a subtle gradient sheen over each fill — a white
                    // top-gloss on unselected buttons and two dark-teal (rgb(0,72,78)) sheen layers on selected ones.
                    // Applied as inline backgroundImage (multi-layer gradients are too fragile as Tailwind arbitrary
                    // values); the base fill color still comes from the cva / override classes, so theming is preserved.
                    const getButtonStyleGradient = (): React.CSSProperties | undefined => {
                        if (!showButtonStyle) return undefined;
                        if (variantState === "selected" || variantState === "buttonsSelected") {
                            return {
                                backgroundImage:
                                    "linear-gradient(246deg, rgba(0,72,78,0.188) 0%, rgba(0,72,78,0) 50%), linear-gradient(180deg, rgba(0,72,78,0.188) 0%, rgba(0,72,78,0) 50%)"
                            };
                        }
                        if (variantState === "default" || variantState === "buttonsDefault") {
                            return { backgroundImage: "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 50%)" };
                        }
                        return undefined;
                    };
                    const buttonStyleGradient = getButtonStyleGradient();

                    const radioGroupItemClassConditions = {
                        "rounded-l-md": (variant === "default" || variant === "floating") && index === 0,
                        "rounded-r-md": (variant === "default" || variant === "floating") && index === options.length - 1,
                        "rounded-l-xl": variant === "floating-inner" && index === 0,
                        "rounded-r-xl": variant === "floating-inner" && index === options.length - 1,
                        "focus:rounded-none": (variant === "default" || variant === "floating" || variant === "floating-inner") && index !== 0 && index !== options.length - 1,
                        "shadow-faux-border-danger": !!error,
                        "focus:text-content-primary": variant === "floating-inner"
                    };

                    const labelClassConditions = {
                        "mt-1": variant !== "floating-inner" && variant !== "buttons",
                        "text-content-secondary": (variant === "floating-inner" || variant === "buttons") && !isSelected,
                        // Remove truncate and w-full for buttons variant to prevent overflow
                        "truncate w-full": variant !== "buttons",
                        "whitespace-nowrap": variant === "buttons"
                    };

                    return (
                        <ToggleGroup.Item
                            id={option.id}
                            data-testid={option.dataTestId || option.id}
                            key={option.value}
                            value={option.value}
                            disabled={disabled}
                            className={cn(
                                radioGroupItem({ state: variantState, variant }),
                                radioGroupItemClassConditions,
                                buttonStyleOverride,
                                radioGroupItemClassName,
                                styles?.item
                            )}
                            style={buttonStyleGradient}
                        >
                            {option.icon}
                            <span className={cn("cursor-pointer text-sm leading-tight", labelClassConditions, styles?.label)}>{option.label}</span>
                            {!error && !!tipText && reimbursementVariant === "descriptor" && showReimbursementTip && (
                                <div className="relative">
                                    <div className="absolute bottom-[-46px] left-1/2 flex -translate-x-1/2 flex-col items-center text-xs text-content-brand-b xs:text-sm lg:text-xs">
                                        {isSelected && (
                                            <FontAwesomeIcon
                                                icon={faTriangle}
                                                data-testid={`reimbursement-badge-indicator-${option.value}`}
                                                className="mb-[-3px] scale-x-150 scale-y-100 text-content-brand-b"
                                                size="sm"
                                            />
                                        )}
                                        <Badge
                                            variant={isSelected ? "brandB" : "ghost"}
                                            data-testid=""
                                            className={cn("py-1 font-medium leading-4", { "text-content-brand-b": !isSelected })}
                                        >
                                            {tipText}
                                        </Badge>
                                    </div>
                                </div>
                            )}
                        </ToggleGroup.Item>
                    );
                })}
            </ToggleGroup.Root>
            {error && (
                <p className="text-red-500 mt-1 text-sm" data-testid="radio-error">
                    {error}
                </p>
            )}
        </div>
    );
};

export default RadioButtonGroup;
